import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  IconButton,
  Tooltip,
  Pagination,
  Chip,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Videocam as VideocamIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateUtils';
import LibraryPreviewModal from '../components/common/LibraryPreviewModal';

const DigitalLibrary = () => {
  const { getAuthHeaders, tokenManager } = useAuth();

  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });

  const [search, setSearch] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  const mediaSrc = (fileId) => `${API_URL}/digital-library/files/${fileId}/view?token=${encodeURIComponent(tokenManager.getToken())}`;
  const coverSrc = (folderId) => `${API_URL}/digital-library/folders/${folderId}/cover?token=${encodeURIComponent(tokenManager.getToken())}`;

  const fetchFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const res = await fetch(`${API_URL}/digital-library/folders`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load folders');
      setFolders(data.folders || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load folders');
    } finally {
      setLoadingFolders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const fetchFiles = useCallback(async (page = 1) => {
    if (!selectedFolder) return;
    setLoadingFiles(true);
    try {
      const params = new URLSearchParams({ page, limit: 24, search, sort });
      if (fileTypeFilter !== 'all') params.append('fileType', fileTypeFilter);

      const res = await fetch(`${API_URL}/digital-library/folders/${selectedFolder.folderId}/files?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load files');
      setFiles(data.files || []);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error.message || 'Failed to load files');
    } finally {
      setLoadingFiles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, search, fileTypeFilter, sort]);

  useEffect(() => {
    if (selectedFolder) fetchFiles(1);
  }, [selectedFolder, search, fileTypeFilter, sort, fetchFiles]);

  const handleOpenFolder = (folder) => {
    setSelectedFolder(folder);
    setSearch('');
    setFileTypeFilter('all');
    setSort('newest');
  };

  const handleBack = () => {
    setSelectedFolder(null);
    setFiles([]);
  };

  const handlePreview = (idx) => {
    setPreviewIndex(idx);
    setPreviewOpen(true);
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(`${API_URL}/digital-library/files/${file.fileId}/download`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error.message || 'Failed to download file');
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '30px' }}>Digital Library</Typography>
      <Typography variant="h6" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 3 }}>
        Browse company photos, videos, and event memories
      </Typography>

      {!selectedFolder ? (
        loadingFolders ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : folders.length === 0 ? (
          <Alert severity="info">No folders have been created yet. Check back soon!</Alert>
        ) : (
          <Grid container spacing={3}>
            {folders.map((folder) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={folder.folderId}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardActionArea onClick={() => handleOpenFolder(folder)}>
                    {folder.coverImageUrl ? (
                      <CardMedia
                        component="img"
                        height="140"
                        image={coverSrc(folder.folderId)}
                        alt={folder.name}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'primary.light',
                        }}
                      >
                        <FolderIcon sx={{ fontSize: 56, color: 'primary.main' }} />
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>{folder.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {folder.fileCount} {folder.fileCount === 1 ? 'file' : 'files'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated {formatDate(folder.updatedAt)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={handleBack}><ArrowBackIcon /></IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{selectedFolder.name}</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search files, tags, description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={fileTypeFilter} onChange={(e) => setFileTypeFilter(e.target.value)}>
                <MenuItem value="all">All types</MenuItem>
                <MenuItem value="image">Images</MenuItem>
                <MenuItem value="video">Videos</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={sort} onChange={(e) => setSort(e.target.value)}>
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
                <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                <MenuItem value="size-desc">Largest first</MenuItem>
                <MenuItem value="size-asc">Smallest first</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loadingFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : files.length === 0 ? (
            <Alert severity="info">No files found in this folder.</Alert>
          ) : (
            <>
              <Grid container spacing={2}>
                {files.map((file, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={file.fileId}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        position: 'relative',
                        '&:hover .library-hover-actions': { opacity: 1 },
                      }}
                    >
                      <Box sx={{ position: 'relative', height: 160, bgcolor: 'grey.200' }}>
                        {file.fileType === 'image' ? (
                          <Box component="img" src={mediaSrc(file.fileId)} alt={file.originalName}
                               sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <VideocamIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                          </Box>
                        )}
                        <Chip
                          size="small"
                          icon={file.fileType === 'video' ? <VideocamIcon /> : <ImageIcon />}
                          label={file.fileType}
                          sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' }}
                        />
                        <Box
                          className="library-hover-actions"
                          sx={{
                            position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.35)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                            opacity: 0, transition: 'opacity 0.2s'
                          }}
                        >
                          <Tooltip title="Preview">
                            <IconButton sx={{ color: '#fff' }} onClick={() => handlePreview(idx)}><VisibilityIcon /></IconButton>
                          </Tooltip>
                          {file.downloadEnabled && (
                            <Tooltip title="Download">
                              <IconButton sx={{ color: '#fff' }} onClick={() => handleDownload(file)}><DownloadIcon /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{file.originalName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(file.createdAt)} • {file.fileSizeFormatted}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={(e, page) => fetchFiles(page)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <LibraryPreviewModal
        open={previewOpen}
        files={files}
        index={previewIndex}
        onClose={() => setPreviewOpen(false)}
        onNavigate={(newIndex) => {
          if (newIndex >= 0 && newIndex < files.length) setPreviewIndex(newIndex);
        }}
        onDownload={handleDownload}
      />
    </Box>
  );
};

export default DigitalLibrary;
